import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import GoogleRoundedIcon from '@mui/icons-material/Google'
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded'
import SmsRoundedIcon from '@mui/icons-material/SmsRounded'
import { useAuth } from '../../context/useAuth'
import { Button } from '../Common/Button'
import { TextInput } from '../Common'
import { PhoneNumberInput } from '../Common/PhoneNumberInput'
import timeliningLogo from '../../assets/logo.png'
import { getWrapperRole } from '../../utils/wrapperRole'
import { isValidSupportedE164Phone } from '../../utils/phoneNumber'
import { firebaseAuth } from '../../services/firebase'
import { NlsLocaleSelect } from '../../nls/NlsLocaleSelect'
import { useNls } from '../../nls/nlsProvider'

type LoginProps = {
  /** Deprecated. Kept for backwards compatibility — ignored. Loading is now
   *  handled by IrisWipeSplash / FullscreenLoader at the app root. */
  isLoading?: boolean
  isLocked?: boolean
}

type PhoneStep = 'INPUT' | 'OTP'

const hasSettledFirebaseSession = async () => {
  if (firebaseAuth.currentUser) return true
  await new Promise((resolve) => window.setTimeout(resolve, 450))
  return Boolean(firebaseAuth.currentUser)
}

const PhoneLogin = ({ isLocked }: { isLocked: boolean }) => {
  const { currentUser, sendPhoneVerification, confirmPhoneCode, unlockSession } = useAuth()
  const { t } = useNls()
  const [step, setStep] = useState<PhoneStep>('INPUT')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [verificationId, setVerificationId] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isBusy, setIsBusy] = useState(false)

  const requestOtp = async () => {
    setError(null)
    if (!isValidSupportedE164Phone(phoneNumber)) {
      setError(t('NLSAuthErrorChoosePhone'))
      return
    }
    setIsBusy(true)
    try {
      const id = await sendPhoneVerification(phoneNumber.trim())
      setVerificationId(id)
      setStep('OTP')
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : t('NLSAuthErrorSendOtp'))
    } finally {
      setIsBusy(false)
    }
  }

  const verifyOtp = async () => {
    if (!verificationId) return
    setError(null)
    if (!/^\d{4,8}$/.test(code.trim())) {
      setError(t('NLSAuthErrorEnterOtp'))
      return
    }
    setIsBusy(true)
    try {
      await confirmPhoneCode(verificationId, code.trim())
    } catch (nextError) {
      if (currentUser || await hasSettledFirebaseSession()) return
      setError(nextError instanceof Error ? nextError.message : t('NLSAuthErrorInvalidOtp'))
      setIsBusy(false)
    }
  }

  if (isLocked) {
    return (
      <>
        <Button variant="primary" size="lg" onClick={unlockSession} icon={<LockOpenRoundedIcon />}>
          {t('NLSAuthActionCheckSession')}
        </Button>
        {error && <p className="authError">{error}</p>}
      </>
    )
  }

  return (
    <div className="phoneLoginForm">
      {step === 'INPUT' ? (
        <>
          <label className="phoneLoginLabel">
            <span>{t('NLSAuthFieldPhoneNumber')}</span>
            <PhoneNumberInput
              value={phoneNumber}
              onChange={setPhoneNumber}
              disabled={isBusy}
              groupClassName="phoneLoginPhoneGroup"
              selectClassName="phoneLoginCountrySelect"
              inputClassName="phoneLoginInput"
              testIdPrefix="login-phone"
            />
          </label>
          <Button
            variant="primary"
            size="lg"
            onClick={requestOtp}
            loading={isBusy}
            icon={<SmsRoundedIcon />}
          >
            {isBusy ? t('NLSAuthActionSendingOtp') : t('NLSAuthActionSendOtp')}
          </Button>
        </>
      ) : (
        <>
          <label className="phoneLoginLabel">
            <span>{t('NLSAuthFieldVerificationCode')}</span>
            <TextInput
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              value={code}
              onChange={(next) => setCode(next.trim())}
              disabled={isBusy}
            />
          </label>
          <Button variant="primary" size="lg" onClick={verifyOtp} loading={isBusy}>
            {isBusy ? t('NLSAuthActionVerifying') : t('NLSAuthActionVerifySignIn')}
          </Button>
          <Button
            type="button"
            variant="link"
            size="sm"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => {
              setStep('INPUT')
              setVerificationId(null)
              setCode('')
              setError(null)
            }}
            disabled={isBusy}
          >
            {t('NLSAuthActionUseDifferentNumber')}
          </Button>
        </>
      )}
      {error && <p className="authError">{error}</p>}
    </div>
  )
}

const GoogleLogin = ({ isLocked }: { isLocked: boolean }) => {
  const { currentUser, signInWithGoogle, unlockSession } = useAuth()
  const { t } = useNls()
  const [error, setError] = useState<string | null>(null)
  const [isSigningIn, setIsSigningIn] = useState(false)

  const handleSignIn = async () => {
    setError(null)

    if (isLocked) {
      unlockSession()
      return
    }

    setIsSigningIn(true)

    try {
      await signInWithGoogle()
    } catch (nextError) {
      if (currentUser || await hasSettledFirebaseSession()) return
      setError(nextError instanceof Error ? nextError.message : t('NLSAuthErrorGoogleSignIn'))
      setIsSigningIn(false)
    }
  }

  return (
    <>
      <Button
        variant="primary"
        size="lg"
        onClick={handleSignIn}
        loading={isSigningIn}
        icon={isLocked ? <LockOpenRoundedIcon /> : <GoogleRoundedIcon />}
      >
        {isLocked
          ? t('NLSAuthActionCheckSession')
          : isSigningIn
            ? t('NLSAuthActionOpeningGoogle')
            : t('NLSAuthActionContinueGoogle')}
      </Button>
      {error && <p className="authError">{error}</p>}
    </>
  )
}

export const Login = ({ isLocked = false }: LoginProps) => {
  const { t } = useNls()
  const { sessionNotice } = useAuth()
  // Wrapper role is set once during main.tsx boot before render — safe to read
  // synchronously here. Falls back to null on web.
  const [wrapperRole, setWrapperRole] = useState(() => getWrapperRole())

  useEffect(() => {
    // Boot race safety: if this component mounts before main.tsx finishes its
    // async App.getInfo() (rare — the bootstrap awaits it before render), pick
    // up the role once it lands.
    if (wrapperRole !== null) return
    const interval = window.setInterval(() => {
      const next = getWrapperRole()
      if (next) {
        setWrapperRole(next)
        window.clearInterval(interval)
      }
    }, 200)
    return () => window.clearInterval(interval)
  }, [wrapperRole])

  const useHelperPhoneFlow = wrapperRole === 'helper'

  return (
    <main className="authScreen">
      <motion.section
        className="authHero"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 120, damping: 22 }}
      >
        <div className="authBrand">
          <img className="brandMark" src={timeliningLogo} alt="" aria-hidden="true" />
          <span>{t('NLSAuthBrandName')}</span>
        </div>
        <NlsLocaleSelect className="authLanguageSelect" />
        <div className="authCopy">
          <p className="eyebrow">
            {useHelperPhoneFlow ? t('NLSAuthEyebrowHelperAccess') : t('NLSAuthEyebrowCloudWorkspace')}
          </p>
          <h1 className="loginHeading text-5xl leading-tight tracking-tight md:text-6xl">
            {useHelperPhoneFlow ? t('NLSAuthTitleHelperPhone') : t('NLSAuthTitleCloudWorkspace')}
          </h1>
          <p className="loginSubtitle max-w-lg text-[var(--muted)] leading-relaxed">
            {useHelperPhoneFlow
              ? t('NLSAuthBodyHelperPhone')
              : t('NLSAuthBodyCloudWorkspace')}
          </p>
          {sessionNotice && (
            <p className="authNotice" data-testid="auth-session-notice" style={{ color: 'var(--danger)', fontWeight: 500, margin: '0.75rem 0' }}>
              {sessionNotice}
            </p>
          )}
        </div>
        {useHelperPhoneFlow ? <PhoneLogin isLocked={isLocked} /> : <GoogleLogin isLocked={isLocked} />}
        <p className="authConsentDisclaimer">
          {t('NLSAuthConsentNoticePrefix')}
          <a href="/terms" className="authConsentLink">{t('NLSAuthConsentTermsLink')}</a>
          {t('NLSAuthConsentAndText')}
          <a href="/privacy" className="authConsentLink">{t('NLSAuthConsentPrivacyLink')}</a>.
        </p>
      </motion.section>
    </main>
  )
}
